import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, LockReset, Save } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/auth.store';
import { UserPermission, UserRole, userService } from '../../services/user.service';

const roleLabel: Record<UserRole, string> = {
  admin: 'Administrador',
  receptionist: 'Recepcionista',
  dentist: 'Dentista',
};

const roleColor: Record<UserRole, 'primary' | 'secondary' | 'success'> = {
  admin: 'primary',
  receptionist: 'secondary',
  dentist: 'success',
};

export default function UsersPage() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<UserPermission[]>([]);
  const [permissionsByRole, setPermissionsByRole] = useState<Record<UserRole, string[]>>({
    admin: [],
    receptionist: [],
    dentist: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [openForm, setOpenForm] = useState(false);
  const [openPassword, setOpenPassword] = useState(false);
  const [editingUser, setEditingUser] = useState<UserPermission | null>(null);
  const [passwordUser, setPasswordUser] = useState<UserPermission | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'receptionist' as UserRole,
    phone: '',
    password: '',
    isActive: true,
  });
  const [newPassword, setNewPassword] = useState('');

  const canManageUsers = useMemo(() => currentUser?.role === 'admin', [currentUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [usersData, permissions] = await Promise.all([
        userService.getUsers(),
        userService.getPermissions(),
      ]);
      setUsers(usersData);
      setPermissionsByRole({
        admin: permissions.find((r) => r.role === 'admin')?.permissions || [],
        receptionist: permissions.find((r) => r.role === 'receptionist')?.permissions || [],
        dentist: permissions.find((r) => r.role === 'dentist')?.permissions || [],
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'receptionist',
      phone: '',
      password: '',
      isActive: true,
    });
    setOpenForm(true);
  };

  const handleEdit = (u: UserPermission) => {
    setEditingUser(u);
    setFormData({
      name: u.name,
      email: u.email,
      role: u.role,
      phone: u.phone || '',
      password: '',
      isActive: u.isActive,
    });
    setOpenForm(true);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      if (editingUser) {
        await userService.updateUser(editingUser.id, {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          phone: formData.phone || null,
          isActive: formData.isActive,
        });
        toast.success('Usuário atualizado com sucesso');
      } else {
        await userService.createUser({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          phone: formData.phone || undefined,
          isActive: formData.isActive,
        });
        toast.success('Usuário cadastrado com sucesso');
      }
      setOpenForm(false);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleToggleActive = async (u: UserPermission) => {
    try {
      setError('');
      await userService.updateUser(u.id, { isActive: !u.isActive });
      await loadData();
      toast.success(`Usuário ${u.isActive ? 'inativado' : 'ativado'} com sucesso`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao atualizar status');
      toast.error('Não foi possível atualizar o status');
    }
  };

  const handleOpenPassword = (u: UserPermission) => {
    setPasswordUser(u);
    setNewPassword('');
    setOpenPassword(true);
  };

  const handleResetPassword = async () => {
    if (!passwordUser) return;
    try {
      setError('');
      await userService.resetPassword(passwordUser.id, newPassword);
      setOpenPassword(false);
      toast.success('Senha redefinida com sucesso');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao resetar senha');
      toast.error('Não foi possível redefinir a senha');
    }
  };

  if (!canManageUsers) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">Acesso restrito</Typography>
        <Typography color="text.secondary">Apenas administradores podem gerenciar usuários.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Usuários e Permissões
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Novo Usuário
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Papel</TableCell>
              <TableCell>Permissões</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Chip size="small" label={roleLabel[u.role]} color={roleColor[u.role]} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {u.permissions.join(', ')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={u.isActive ? 'Ativo' : 'Inativo'} color={u.isActive ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => handleEdit(u)}>
                      Editar
                    </Button>
                    <Button size="small" onClick={() => handleOpenPassword(u)} startIcon={<LockReset />}>
                      Senha
                    </Button>
                    <Button size="small" color={u.isActive ? 'warning' : 'success'} onClick={() => handleToggleActive(u)}>
                      {u.isActive ? 'Inativar' : 'Ativar'}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">Nenhum usuário encontrado.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Matriz de Permissões por Papel
        </Typography>
        {(['admin', 'receptionist', 'dentist'] as UserRole[]).map((role) => (
          <Box key={role} sx={{ mb: 1 }}>
            <Typography variant="subtitle2">{roleLabel[role]}</Typography>
            <Typography variant="body2" color="text.secondary">
              {permissionsByRole[role].join(', ')}
            </Typography>
          </Box>
        ))}
      </Paper>

      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Nome"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
            {!editingUser && (
              <TextField
                label="Senha inicial"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                fullWidth
                helperText="Mínimo de 6 caracteres"
              />
            )}
            <TextField
              label="Telefone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Papel</InputLabel>
              <Select
                value={formData.role}
                label="Papel"
                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              >
                <MenuItem value="admin">Administrador</MenuItem>
                <MenuItem value="receptionist">Recepcionista</MenuItem>
                <MenuItem value="dentist">Dentista</MenuItem>
              </Select>
            </FormControl>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <Typography>Usuário ativo</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={
              !formData.name ||
              !formData.email ||
              (!editingUser && formData.password.length < 6)
            }
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openPassword} onClose={() => setOpenPassword(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Resetar Senha</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Nova senha"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Mínimo de 6 caracteres"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPassword(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={newPassword.length < 6}>
            Atualizar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
